import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, Sliders } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const CoverageRulesSection = () => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // area_key -> {capacity_required, active}

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/coverage-rules`);
      setGroups(res.data.groups || []);
    } catch (e) {
      toast.error('Failed to load coverage rules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRules(); }, []);

  const draftFor = (rule) => drafts[rule.area_key] || {
    capacity_required: rule.capacity_required,
    active: rule.active,
    supports_side: rule.supports_side,
  };

  const setDraft = (area_key, patch) =>
    setDrafts((d) => ({ ...d, [area_key]: { ...draftFor({ area_key, ...d[area_key] }), ...(d[area_key] || {}), ...patch } }));

  const saveRule = async (rule) => {
    const d = drafts[rule.area_key];
    if (!d) return;
    try {
      const payload = {};
      if (d.capacity_required !== undefined && d.capacity_required !== '') payload.capacity_required = parseInt(d.capacity_required, 10);
      if (d.active !== undefined) payload.active = d.active;
      if (d.supports_side !== undefined) { payload.supports_side = d.supports_side; payload.supports_both = d.supports_side; }
      await axios.put(`${API_BASE_URL}/api/admin/coverage-rules/${rule.area_key}`, payload);
      toast.success(`Updated ${rule.name}`);
      setDrafts((prev) => { const n = { ...prev }; delete n[rule.area_key]; return n; });
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update rule');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="tc-spinner" /><span className="ml-4 text-primary">Loading rules…</span></div>;
  }

  return (
    <div className="space-y-6" data-testid="coverage-rules-section">
      <div>
        <h2 className="text-3xl font-bold text-primary mb-1 flex items-center gap-2"><Sliders className="w-6 h-6 text-amber-400" /> Installation Coverage Rules</h2>
        <p className="text-secondary text-sm">Configure the TC Roll Credit value for each package and individual vehicle area. Changes only affect new certificates — historical consumption is preserved.</p>
      </div>

      {groups.map((g) => (
        <div key={g.category} className="tc-card p-0 overflow-hidden">
          <div className="px-4 py-3 bg-black/40 border-b border-amber-500/20">
            <h3 className="text-lg font-semibold text-amber-400">{g.label}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted text-xs border-b border-slate-700">
                  <th className="text-left px-4 py-2">Installation Area</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Credit Value</th>
                  <th className="text-left px-4 py-2">Supports Side / Both</th>
                  <th className="text-left px-4 py-2">Active</th>
                  <th className="text-left px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {g.rules.map((rule) => {
                  const d = draftFor(rule);
                  const dirty = !!drafts[rule.area_key];
                  return (
                    <tr key={rule.area_key} className="border-b border-slate-800 hover:bg-black/20" data-testid={`rule-row-${rule.area_key}`}>
                      <td className="px-4 py-2 text-primary font-medium">{rule.name}</td>
                      <td className="px-4 py-2 text-secondary capitalize">{rule.coverage_type}</td>
                      <td className="px-4 py-2">
                        {rule.requires_review ? (
                          <span className="text-amber-400 text-xs">Admin review</span>
                        ) : (
                          <input type="number" min="0"
                            className="tc-input tc-focus-ring w-20 py-1"
                            data-testid={`rule-credit-${rule.area_key}`}
                            value={d.capacity_required ?? ''}
                            onChange={(e) => setDraft(rule.area_key, { capacity_required: e.target.value })} />
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <label className="flex items-center gap-2 text-xs text-secondary cursor-pointer">
                          <input type="checkbox" checked={!!d.supports_side}
                            data-testid={`rule-side-${rule.area_key}`}
                            onChange={(e) => setDraft(rule.area_key, { supports_side: e.target.checked })} />
                          Driver / Passenger / Both
                        </label>
                      </td>
                      <td className="px-4 py-2">
                        <label className="flex items-center gap-2 text-xs cursor-pointer">
                          <input type="checkbox" checked={!!d.active}
                            data-testid={`rule-active-${rule.area_key}`}
                            onChange={(e) => setDraft(rule.area_key, { active: e.target.checked })} />
                          <span className={d.active ? 'text-green-400' : 'text-red-400'}>{d.active ? 'Active' : 'Inactive'}</span>
                        </label>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          onClick={() => saveRule(rule)}
                          disabled={!dirty}
                          data-testid={`rule-save-${rule.area_key}`}
                          className={`flex items-center gap-1 text-xs px-3 py-1.5 rounded-md ${dirty ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-slate-800 text-slate-500 cursor-not-allowed'}`}>
                          <Save className="w-3 h-3" /> Save
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CoverageRulesSection;
